import React, { useMemo, useState } from 'react';
import { Box, Grid, Alert, CircularProgress, useTheme } from '@mui/material';
import FavoriteIcon from '@mui/icons-material/Favorite';
import BedtimeIcon from '@mui/icons-material/Bedtime';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import { StatCard, DonutChart } from '../../../../../shared/components/charts';
import { DashboardCard, CohortGuard, MetricBreakdownPanel } from '../../../../../shared/components';
import { useMetricsStats } from '../../../../../shared/hooks';
import { toComparisonKey, getMetricDefinition } from '../../../../../shared/utils';
import type { MetricsFilters } from '../../../../../shared/api/types';
import { useRecoveryStats, useRecoveryScoreDistribution } from '../hooks';

interface RecoveryStatsColumnProps {
  filterParams: MetricsFilters;
  isCentralAdmin: boolean;
  isAnonymised: boolean;
  /** When true, stat cards stack vertically (comparison columns). */
  stacked?: boolean;
}

/**
 * Renders Recovery's stat cards + recovery-score distribution for a single
 * filter scope (one comparison group, or the whole "average" cohort). Calls the
 * existing Recovery hooks once, so it is safe to render one per group.
 */
export const RecoveryStatsColumn: React.FC<RecoveryStatsColumnProps> = ({
  filterParams,
  isCentralAdmin,
  isAnonymised,
  stacked = false,
}) => {
  const theme = useTheme();
  const statsQuery = useRecoveryStats(filterParams);
  const distributionQuery = useRecoveryScoreDistribution(filterParams);
  // Per-group cohort count for the privacy guard.
  const cohortQuery = useMetricsStats({ ...filterParams, metrics: ['active_users'] });
  const activeUserCount = cohortQuery.data?.data?.active_users?.value;

  const [expandedMetric, setExpandedMetric] = useState<string | null>(null);
  const toggleMetric = (metricId: string) => setExpandedMetric((prev) => (prev === metricId ? null : metricId));

  const statCards = useMemo(() => {
    if (!statsQuery.data?.data) return [];
    const stats = statsQuery.data.data;
    return [
      { metricId: 'recovery_score', metric: stats.recovery_score, icon: FavoriteIcon },
      { metricId: 'hrv', metric: stats.hrv, icon: MonitorHeartIcon },
      { metricId: 'resting_heart_rate', metric: stats.resting_heart_rate, icon: VolunteerActivismIcon },
      { metricId: 'sleep_performance', metric: stats.sleep_performance, icon: BedtimeIcon },
    ].filter((card) => card.metric);
  }, [statsQuery.data]);

  const distributionData = useMemo(() => {
    if (!distributionQuery.data?.data?.distribution) return [];
    return distributionQuery.data.data.distribution.map((item) => ({
      label: item.label,
      value: item.value,
      color:
        item.name === 'high' || item.label.includes('67-100')
          ? theme.palette.success.main
          : item.name === 'medium' || item.label.includes('34-66')
            ? theme.palette.warning.main
            : theme.palette.error.main,
    }));
  }, [distributionQuery.data, theme]);

  const isLoading = statsQuery.isLoading || distributionQuery.isLoading;

  if (statsQuery.isError) {
    return <Alert severity='error'>Failed to load recovery data.</Alert>;
  }

  // In comparison columns the stat cards stack; in average mode they sit in the
  // usual 4-up responsive grid.
  const statColSize = stacked ? { xs: 12 } : { xs: 12, sm: 6, md: 3 };

  return (
    <Box>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {isLoading
          ? [1, 2, 3, 4].map((i) => (
              <Grid size={statColSize} key={i}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    py: 3,
                  }}
                >
                  <CircularProgress size={24} />
                </Box>
              </Grid>
            ))
          : statCards.map((stat) => {
              const breakdownSupported = toComparisonKey(stat.metricId) !== null;
              const open = expandedMetric === stat.metricId;
              const unit = stat.metricId === 'hrv' ? 'ms' : stat.metricId === 'resting_heart_rate' ? 'bpm' : '%';
              return (
                <Grid size={statColSize} key={stat.metricId}>
                  <StatCard
                    metric={stat.metric}
                    icon={stat.icon}
                    expandable={breakdownSupported}
                    expanded={open}
                    onToggleExpand={() => toggleMetric(stat.metricId)}
                    breakdown={
                      <MetricBreakdownPanel
                        metricId={stat.metricId}
                        metricLabel={getMetricDefinition(stat.metricId)?.plainName ?? stat.metric.label}
                        unit={unit}
                        filterParams={filterParams}
                        enabled={open}
                      />
                    }
                  />
                </Grid>
              );
            })}
      </Grid>

      <DashboardCard
        title='Recovery Score Distribution'
        subtitle='Proportion of employees in each recovery range.'
        metricKey='recovery_score'
        expandable={false}
      >
        <CohortGuard userCount={activeUserCount} bypass={!isAnonymised} loading={statsQuery.isLoading}>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 260 }}>
              <CircularProgress />
            </Box>
          ) : distributionData.length > 0 ? (
            <DonutChart data={distributionData} height={260} />
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 260 }}>
              <Alert severity='info'>No distribution data available</Alert>
            </Box>
          )}
        </CohortGuard>
      </DashboardCard>
    </Box>
  );
};
